import React, { useState, useEffect, useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SnapShareConfirmCard — confirm card for `pending_action.type === "share_to_snap"`.
 *
 * Mirrors TweetConfirmCard's UX so users have ONE consistent confirm flow
 * across all social platforms (X, Snap, future TikTok/Instagram).
 *
 * Snap has NO server-side write API. The "share" itself is a CLIENT-SIDE
 * deep link via Snap Creative Kit Web: the user taps "Send to Snapchat" and
 * Snap's official `snapchat-creative-kit-share` button opens Snapchat with
 * our sticker + share URL attached. We render that button hidden and
 * programmatically click it.
 *
 * Props
 * -----
 *  - pending: { caption_text, wallpaper_id, char_count }
 *  - onSent / onCancelled: callbacks for the parent to mark resolution.
 */
export function SnapShareConfirmCard({ pending, onSent, onCancelled }) {
  const [text, setText] = useState(pending?.caption_text || "");
  const [posting, setPosting] = useState(false);
  const [status, setStatus] = useState(null); // 'sent' | 'cancelled' | 'error' | null
  const [errorMsg, setErrorMsg] = useState("");
  const snapBtnRef = useRef(null);

  const charCount = text.length;
  const overLimit = charCount > 250;

  // Phase 29.A/29.H — Build the share URL Snap will scrape for OG/snap meta tags.
  // Supports both wallpaper (default) and user-uploaded photo attachments.
  // Falls back to the public app root if nothing is attached.
  const attachmentKind = pending?.attachment_kind || (pending?.wallpaper_id ? "wallpaper" : null);
  const attachmentId = pending?.attachment_id || pending?.wallpaper_id || null;
  const shareUrl = attachmentId && attachmentKind === "user_photo"
    ? `${window.location.origin}/api/share/photo/${attachmentId}`
    : attachmentId && attachmentKind === "wallpaper"
      ? `${window.location.origin}/api/share/wallpaper/${attachmentId}`
      : window.location.origin;

  // Re-trigger Snap's SDK to scan for new share buttons after this card mounts.
  useEffect(() => {
    if (status) return;
    const t = setTimeout(() => {
      try {
        if (window.snapKitInit && typeof window.snapKitInit === "function") {
          window.snapKitInit();
        }
      } catch (_) { /* ignore */ }
    }, 80);
    return () => clearTimeout(t);
  }, [status]);

  if (status === "sent") {
    return (
      <div className="tweet-confirm-card sent" data-testid="snap-confirm-card-sent">
        <span>✓ Opened Snapchat — caption + sticker attached.</span>
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="tweet-confirm-card cancelled" data-testid="snap-confirm-card-cancelled">
        ↩ Snap cancelled — nothing shared.
      </div>
    );
  }

  return (
    <div className="tweet-confirm-card snap-confirm-card" data-testid="snap-confirm-card">
      <div className="tweet-confirm-header">
        <span className="tweet-confirm-bird" style={{ color: "#FFFC00" }}>👻</span>
        <span className="tweet-confirm-label">Ready to share to Snapchat</span>
        <span className={`tweet-confirm-count ${overLimit ? "over" : ""}`}>
          {charCount}/250
        </span>
      </div>
      <textarea
        className="tweet-confirm-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={300}
        data-testid="snap-confirm-text"
      />
      {errorMsg && <div className="tweet-confirm-error">{errorMsg}</div>}

      {/* Hidden official Snap Creative Kit share button — we click it. */}
      <div style={{ display: "none" }}>
        <div
          ref={snapBtnRef}
          className="snapchat-creative-kit-share"
          data-share-url={shareUrl}
          data-size="large"
          data-text={text}
          data-testid="snap-creative-kit-hidden-btn"
        />
      </div>

      <div className="tweet-confirm-actions">
        <button
          className="tweet-confirm-cancel"
          onClick={() => { setStatus("cancelled"); onCancelled?.(); }}
          disabled={posting}
          data-testid="snap-confirm-cancel"
        >
          Cancel
        </button>
        <button
          className="tweet-confirm-send"
          disabled={posting || overLimit || !text.trim()}
          data-testid="snap-confirm-send"
          onClick={async () => {
            setPosting(true);
            setErrorMsg("");
            try {
              // Update the data-text attribute on the Snap button with the
              // final caption, then click it to open Snapchat.
              const el = snapBtnRef.current;
              if (el) {
                el.setAttribute("data-text", text);
                el.setAttribute("data-share-url", shareUrl);
                // Snap's SDK transforms the div into an <iframe>/<a> on init.
                // Find the inner clickable element and click it.
                const clickable =
                  el.querySelector("a") ||
                  el.querySelector("iframe") ||
                  el.querySelector("button") ||
                  el;
                try { clickable.click(); } catch (_) { /* ignore */ }
              } else {
                // Fallback: direct Snapchat deep link (web URL scheme)
                const deep =
                  `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(shareUrl)}`;
                window.open(deep, "_blank", "noopener");
              }
              setStatus("sent");
              onSent?.();
            } catch (e) {
              setErrorMsg("Couldn't open Snapchat — try again?");
            } finally {
              setPosting(false);
            }
          }}
        >
          {posting ? "Opening Snapchat…" : "Send to Snapchat"}
        </button>
      </div>
    </div>
  );
}
